import { cx, useResizeObserver, useTranslations } from "@jugl-web/utils";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { CRM_EMAIL_MESSAGE_EXPANDED_KEY_PREFIX } from "@jugl-web/utils/storage";
import { PlainButton } from "@jugl-web/ui-kit";
import React, { useCallback, useMemo, useState } from "react";
import { emailBodyToHtml } from "@web-src/modules/client-support/utils/emailBodyToHtml";
import { ReactComponent as ArrowDownIcon } from "./assets/arrow-down.svg";

// keep collapsed bodies short so the virtualized message list stays stable;
// taller bodies are clipped behind a Show more / Show less toggle
const MAX_COLLAPSED_HEIGHT = 300;

// HTML emails carry their own layout — usually nested tables — so they get
// nothing beyond the base type and an image guard
const EMAIL_BASE_CLASS_NAME =
  "font-secondary text-sm leading-[160%] wrap-break-word text-dark-800 [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:rounded-lg";

// markdown output has no styling of its own, and Tailwind's preflight strips
// list markers and blockquote indents
const EMAIL_MARKDOWN_CLASS_NAME = cx(
  EMAIL_BASE_CLASS_NAME,
  "[&_p]:my-0 [&_p+p]:mt-3",
  "[&_h1]:my-3 [&_h1]:text-lg [&_h1]:font-medium [&_h2]:my-3 [&_h2]:text-base [&_h2]:font-medium [&_h3]:my-3 [&_h3]:text-sm [&_h3]:font-medium",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_hr]:my-3 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-dark-100",
  "[&_pre]:whitespace-pre-wrap [&_code]:rounded [&_code]:bg-grey-200 [&_code]:px-1",
  "[&_blockquote]:my-2 [&_blockquote]:border-0 [&_blockquote]:border-l-2 [&_blockquote]:border-solid [&_blockquote]:border-dark-200 [&_blockquote]:pl-3 [&_blockquote]:text-dark-600",
  // tables scroll on their own so a wide email can't stretch the bubble
  "[&_table]:my-2 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse",
  "[&_td]:border [&_td]:border-solid [&_td]:border-dark-200 [&_td]:px-2 [&_td]:py-1",
  "[&_th]:border [&_th]:border-solid [&_th]:border-dark-200 [&_th]:px-2 [&_th]:py-1 [&_th]:font-medium"
);

export const EmailMessageBody: React.FC<{
  messageId: string;
  text: string;
}> = ({ messageId, text }) => {
  const { t } = useTranslations();

  const { html, trimmedHtml, isHtmlSource } = useMemo(
    () => emailBodyToHtml(text),
    [text]
  );

  const bodyClassName = isHtmlSource
    ? EMAIL_BASE_CLASS_NAME
    : EMAIL_MARKDOWN_CLASS_NAME;

  const [isExpanded, setIsExpanded] = useSessionStorage(
    `${CRM_EMAIL_MESSAGE_EXPANDED_KEY_PREFIX}${messageId}`,
    false
  );

  const [isTrimmedVisible, setIsTrimmedVisible] = useState(false);

  // measure the inner (never height-clipped) node so the result is stable
  // whether collapsed or expanded
  const [isOverflowing, setIsOverflowing] = useState(false);
  const handleResize = useCallback(
    (entry: ResizeObserverEntry) =>
      setIsOverflowing(entry.target.scrollHeight > MAX_COLLAPSED_HEIGHT),
    []
  );
  const { ref } = useResizeObserver<HTMLDivElement>({ onResize: handleResize });

  const isCollapsed = !isExpanded && isOverflowing;

  return (
    // when the toggle is shown, match the bottom padding to the gap above it
    <div className={cx("px-6", isOverflowing ? "pb-3.5" : "pb-5")}>
      <div
        className={cx(
          "relative",
          isCollapsed && "max-h-[300px] overflow-hidden"
        )}
      >
        <div
          ref={ref}
          className={bodyClassName}
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {isCollapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[30px] bg-linear-to-t from-white to-transparent" />
        )}
      </div>
      {trimmedHtml && (
        <div className="mt-3">
          <PlainButton
            onClick={() => setIsTrimmedVisible((value) => !value)}
            aria-label={
              isTrimmedVisible
                ? t({
                    id: "client-support-page.hide-trimmed-content",
                    defaultMessage: "Hide trimmed content",
                  })
                : t({
                    id: "client-support-page.show-trimmed-content",
                    defaultMessage: "Show trimmed content",
                  })
            }
            className="flex h-4.5 items-center rounded bg-grey-200 px-1.5 text-xs leading-none font-medium text-dark-600 transition-colors hover:bg-grey-300"
          >
            •••
          </PlainButton>
          {isTrimmedVisible && (
            <div
              className={cx(
                "mt-3 border-0 border-l-2 border-solid border-dark-200 pl-3",
                bodyClassName,
                "text-dark-600"
              )}
              // oxlint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: trimmedHtml }}
            />
          )}
        </div>
      )}
      {isOverflowing && (
        <>
          <div className="-mx-6 mt-3.5 h-px bg-dark-100" />
          <PlainButton
            onClick={() => setIsExpanded((value) => !value)}
            className="mt-3.5 flex items-center gap-1.5 font-secondary text-sm font-medium text-primary"
          >
            {isExpanded
              ? t({
                  id: "client-support-page.read-less",
                  defaultMessage: "Read Less",
                })
              : t({
                  id: "client-support-page.read-more",
                  defaultMessage: "Read More",
                })}
            <ArrowDownIcon
              className={cx(
                "shrink-0 transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          </PlainButton>
        </>
      )}
    </div>
  );
};
